import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import { ParentService } from 'us-web-services';
import { Button, ButtonType } from '../../components';
import { iconUrls } from '../../constants/IconUrls';
import { useDependencies } from '../../dependencies';
import { useModal } from '../modalContext';

export const AddPetCareContent = (): JSX.Element => {
  const { stores } = useDependencies();
  const modal = useModal();
  const { t } = useTranslation();
  const { parent } = stores.parent;
  const [dog, setDog] = useState<boolean | undefined>();
  const [cat, setCat] = useState<boolean | undefined>();
  const [otherPet, setOtherPet] = useState<boolean | undefined>();

  const closeModal = (): void => {
    modal?.setModalName(undefined);
  }

  useEffect(() => {
    if (parent.dog !== undefined) {
      setDog(parent.dog);
    }
    if (parent.cat !== undefined) {
      setCat(parent.cat);
    }
    if (parent.otherPet !== undefined) {
      setOtherPet(parent.otherPet);
    }
  }, []);

  const handlePetSelected = (type: string): void => {
    switch (type) {
      case 'dog':
        parent.dog = !dog;
        setDog(!dog);
        break;
      case 'cat':
        parent.cat = !cat;
        setCat(!cat);
        break;
      case 'otherPet':
      default:
        parent.otherPet = !otherPet;
        setOtherPet(!otherPet);
    }
  }

  const savePets = async (): Promise<void> => {
    const config = {
      id: parent.id,
      cat,
      dog,
      otherPet,
    };

    await ParentService.patch(parent?.id || 0, config).then(() => {
      stores.parent.updateParent(parent);
      closeModal();
    });
  }

  const getIconUrl = (type: string): string => {
    switch (type) {
      case 'dog':
        if (dog !== null) {
          return dog ? iconUrls.dogYes : iconUrls.dogNo;
        }
        return iconUrls.dogUnknown;
      case 'cat':
        if (cat !== null) {
          return cat ? iconUrls.catYes : iconUrls.catNo;
        }
        return iconUrls.catUnknown;
      case 'otherPet':
      default:
        if (otherPet !== null) {
          return otherPet ? iconUrls.otherYes : iconUrls.otherNo;
        }
        return iconUrls.otherUnknown;
    }
  }

  const getPetYesOrNo = (type: string): string => {
    switch (type) {
      case 'dog':
        if (dog !== null) {
          return dog ? t('dashboard.widgets.addPetCare.yes') : t('dashboard.widgets.addPetCare.no');
        }
        return '';
      case 'cat':
        if (cat !== null) {
          return cat ? t('dashboard.widgets.addPetCare.yes') : t('dashboard.widgets.addPetCare.no');
        }
        return '';
      case 'otherPet':
      default:
        if (otherPet !== null) {
          return otherPet ? t('dashboard.widgets.addPetCare.yes') : t('dashboard.widgets.addPetCare.no');
        }
        return '';
    }
  }

  return (
    <div>
      <div className='flex justify-between w:full h-10 bg-sapphire text-white font-bold'>
        <div className='float-left mt-2 ml-2 text-lg'>{t('dashboard.widgets.addPetCare.heading')}</div>
        <Button aria-label='Close modal' className='float-right mr-2' onClick={closeModal}>
          <FontAwesomeIcon icon={faXmark} />
        </Button>
      </div>
      <div className='flex flex-col mt-2 mb-1 place-items-center'>
        <div className='text-neutral-300 mb-1'>{t('dashboard.widgets.addPetCare.title')}</div>
        <div className='flex justify-between'>
          <button
            className='inline-block align-middle m-6 text-slate-700'
            type='button'
            onClick={(): void => handlePetSelected('dog')}
          >
            <img src={getIconUrl('dog')} alt='dogUnknown' />
            <div>{getPetYesOrNo('dog')} {t('dashboard.widgets.addPetCare.dogs')}</div>
          </button>
          <button
            className='inline-block align-middle m-6 text-slate-700'
            type='button'
            onClick={(): void => handlePetSelected('cat')}
          >
            <img src={getIconUrl('cat')} alt='catUnknown' />
            <div>{getPetYesOrNo('cat')} {t('dashboard.widgets.addPetCare.cats')}</div>
          </button>
          <button
            className='inline-block align-middle m-6 text-slate-700'
            type='button'
            onClick={(): void => handlePetSelected('otherPet')}
          >
            <img src={getIconUrl('otherPet')} alt='otherUnknown' />
            <div>{getPetYesOrNo('otherPet')} {t('dashboard.widgets.addPetCare.others')}</div>
          </button>
        </div>
        <div className='flex items-center p-1'>
          <Button
            buttonType={ButtonType.primary}
            disabled={!dog && !cat && !otherPet}
            className='mb-4 bg-sapphire'
            onClick={savePets}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
